import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  User,
  UserPlus,
  AlertCircle,
  History,
  Calendar,
  Building2,
} from 'lucide-react';
import {
  Input,
  Button,
  EmptyState,
  Skeleton,
  Eyebrow,
} from '@insolech/ui';
import {
  patientQueries,
  orderQueries,
  type PatientSummaryRow,
  type OrderPatientMatch,
} from '@insolech/sdk';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';
import {
  PatientForm,
  EMPTY_PATIENT_FORM,
  ORTHOTIC_INTENDED_USE_OPTIONS,
  type PatientFormState,
  type FootSizeUnit,
  type OrthoticIntendedUse,
} from './PatientForm';
import { PriorOrderPickerModal } from './PriorOrderPickerModal';
import { formatProductLabel } from '../productLabel';
import { shouldResetPrescriptionOnModeSwitch } from './patientModeReset';
import { mergePatientSearchResults } from './patientSearchMerge';
import {
  hydrateMetricsFromLines,
  type MetricSource,
} from './patientMetricHydration';
import type { PrescriptionFormState } from './prescriptionFormDefaults';
import type { PrescriptionDeepState } from './prescription/prescriptionState';
import type { SolutionSubtype } from './SolutionSubtypePicker';
import { intersectDeepByTarget } from './prescriptionReuse';

/**
 * Step 2 (custom branch only) - Patient picker.
 *
 * Per Phase 4b iteration UX changes:
 *   - The first thing the user sees is an explicit "New patient vs Existing
 *     patient" mode selector. New is the default - Rafael says it's the most
 *     common case for podiatrists creating prescriptions.
 *   - "Existing patient" opens a search field and result list. Picking one
 *     pre-fills the new PatientForm, which is fully editable. A "Save changes"
 *     CTA at the bottom persists edits to the existing patient row.
 *
 * The wizard captures `patient_mode = 'new' | 'existing'` and either
 * `newPatient` (full form state) or `patientId` + optional patch.
 */

export type PatientMode = 'new' | 'existing';

export interface PatientPickerValue {
  /** 'new' (create on submit) or 'existing' (use patientId, optionally patch). */
  mode: PatientMode;
  /** Set when mode='new' OR mode='existing' with edits applied. */
  formState: PatientFormState;
  /** Set when mode='existing'. */
  patientId?: string | undefined;
  /** Cached display name so the wizard summary can render without a refetch. */
  display_name?: string | undefined;
  /** Cached friendly patient code (e.g. P-26-00001) for an existing patient. */
  patient_code?: string | undefined;
  /** True when an existing patient was selected and the form was edited. */
  hasEdits?: boolean | undefined;
  /**
   * Snapshot of the fields we actually READ off the patients row when the
   * record was picked. PatientSummaryRow carries no `notes`, and `age` here is
   * DERIVED from date_of_birth, so submitting the form as-is would write a
   * blank note and a Jan-1 approximation over real data. Submit compares
   * against this to send only fields the operator genuinely changed.
   */
  recordSnapshot?:
    | {
        age: string;
        notesLoaded: boolean;
        /** Ownership as read off the row. Submit needs BOTH to decide whether
         *  promoting the record to private would hide it from someone - a
         *  private patient is visible only to its creator, and nothing in the
         *  app can un-private it. Undefined when the lookup did not run. */
        isPrivate?: boolean | undefined;
        createdBy?: string | null | undefined;
      }
    | undefined;
  /**
   * Set when this "existing patient" selection was prefilled from a past
   * order (the reorder-by-name flow). There is NO patient record behind it -
   * the migrated history stores names on `order_lines.patient_name`, not in
   * `patients` - so `patientId` stays undefined and the new order is created
   * fresh from the prefilled name + prescription. Drives the "reordering from
   * order X" banner + the prefilled-form panel.
   */
  reorderFromOrder?: ReorderSource | undefined;
  /**
   * Order number whose prescription was copied onto this draft by the
   * prior-order modal. This is the EXPLICIT flag setMode needs: a record-backed
   * pick that seeded a prescription leaves `reorderFromOrder` undefined, so
   * without this, switching to "New patient" cleared the patient but kept the
   * previous patient's prescription - and every prescription section is
   * collapsed by default, so the carryover would be invisible right up to
   * manufacture. Never key this off `patientId`, which only means "a record is
   * selected", not "this panel seeded a prescription".
   *
   * Declared and honoured in PR A so the guard exists before the reuse path
   * here can set it.
   */
  prescriptionSeededFrom?: string | undefined;
}

/** Provenance for a reorder prefill - just enough to render the banner. */
export interface ReorderSource {
  orderId: string;
  orderNumber: string;
  createdAt: string;
  productLabel: string;
}

/**
 * Payload emitted when the operator picks a past order to reorder from. The
 * wizard applies both halves atomically: the patient form fields AND the
 * compatible prescription cards. Prefill only - the source order is untouched.
 */
export interface ReorderPrefill {
  patient: PatientPickerValue;
  prescriptionPatch: Partial<PrescriptionFormState>;
  /** Order number a prescription was copied FROM, when this prefill came from
   *  the prior-order modal. The wizard surfaces it, because every prescription
   *  section is collapsed by default and otherwise a successful copy and a
   *  no-op look exactly the same. */
  appliedFromOrderNumber?: string | undefined;
}

/** Years from a YYYY-MM-DD date string to today, floored. */
function ageFromDob(dob: string): string {
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const monthsBefore =
    now.getMonth() < d.getMonth() ||
    (now.getMonth() === d.getMonth() && now.getDate() < d.getDate());
  if (monthsBefore) age -= 1;
  return age >= 0 && age < 200 ? String(age) : '';
}

/** Build the picker value for selecting an existing patient - shared by the
 *  Existing-patient search and the duplicate-detection banner. */
function buildExistingPatientValue(
  prev: PatientPickerValue,
  row: PatientSummaryRow,
  /** The record's real `notes`, when we managed to load it. `undefined` means
   *  the lookup did not run or failed, and submit must then leave notes alone
   *  rather than overwrite an unseen value. */
  loadedNotes?: string | null | undefined,
  ownership?: { isPrivate: boolean; createdBy: string | null } | undefined,
): PatientPickerValue {
  const notesLoaded = loadedNotes !== undefined;
  return {
    ...prev,
    mode: 'existing',
    patientId: row.id,
    display_name: row.full_name,
    patient_code: row.patient_code ?? undefined,
    hasEdits: false,
    // Explicit, not incidental: `...prev` would otherwise carry a stale
    // "this panel seeded the prescription" flag onto a DIFFERENT patient.
    // Today the search list early-returns once patientId is set, so you cannot
    // reach here with the flag armed - but that is a safety property living
    // three hundred lines away, and this makes it structural.
    prescriptionSeededFrom: undefined,
    recordSnapshot: {
      age: row.date_of_birth ? ageFromDob(row.date_of_birth) : '',
      // PatientSummaryRow carries no notes column, so the pick handler fetches
      // the real one separately. When that succeeded we show it in the form and
      // an edit is a genuine edit; when it did not, notesLoaded stays false and
      // submit omits the column entirely instead of replacing what it never read.
      notesLoaded,
      ...(ownership
        ? { isPrivate: ownership.isPrivate, createdBy: ownership.createdBy }
        : {}),
    },
    formState: {
      full_name: (row.full_name ?? '').trim(),
      age: row.date_of_birth ? ageFromDob(row.date_of_birth) : '',
      weight_kg: '',
      left_foot_size: '',
      left_foot_unit: 'EU',
      right_foot_size: '',
      right_foot_unit: 'EU',
      // R32+ per-order fields default fresh on every reorder. The intended
      // use starts EMPTY: it is a decision about this order, and the form no
      // longer preselects one (validatePatientForm requires it for an insole).
      orthotic_intended_use: '',
      shoe_brand: '',
      units: '1',
      gender: (row.gender as PatientFormState['gender']) ?? '',
      phone: row.phone ?? '',
      email: row.email ?? '',
      // Seeded with the record's real note when we loaded it, so the operator
      // edits what is actually stored instead of typing into a blank box that
      // silently replaces it. Blank + notesLoaded=false tells submit to omit
      // the column. (See resolvePatientForSubmit.)
      observations: loadedNotes ?? '',
    },
  };
}

export const DEFAULT_PATIENT_PICKER_VALUE: PatientPickerValue = {
  mode: 'new',
  formState: EMPTY_PATIENT_FORM,
};

/**
 * Adopt a `patients` row into the picker value. Two things have to happen here
 * and neither can be skipped:
 *
 *  1. CLINIC GUARD. Admins search across every clinic they can see (the search
 *     is deliberately not scoped, because scoping it is what disabled duplicate
 *     detection for them). That makes it possible to pick a record belonging to
 *     a different clinic than the one this order is being raised for, which
 *     then writes the order's edits onto another clinic's patient and only
 *     fails later, inside create_order_with_lines. Refuse the pick instead.
 *  2. LOAD THE REAL NOTE. PatientSummaryRow has no `notes`, so without this the
 *     form starts blank and anything typed replaces a note nobody ever saw.
 *     On failure we return notes as `undefined`, which keeps notesLoaded false
 *     and makes submit omit the column entirely.
 */
async function adoptPatientRecord(
  prev: PatientPickerValue,
  row: PatientSummaryRow,
  orderClinicId: string | null,
): Promise<{ ok: true; value: PatientPickerValue } | { ok: false; reason: string }> {
  if (orderClinicId && row.clinic_id !== orderClinicId) {
    return {
      ok: false,
      reason:
        'That patient record belongs to a different clinic. Switch this order to that clinic, or create a new patient here.',
    };
  }
  let notes: string | null | undefined;
  let ownership: { isPrivate: boolean; createdBy: string | null } | undefined;
  try {
    const detail = await patientQueries.patientDetailQuery(supabase, row.id).queryFn();
    if (detail) {
      notes = detail.notes;
      ownership = { isPrivate: detail.is_private, createdBy: detail.created_by };
    }
  } catch {
    // Leave both undefined: submit will omit `notes` rather than overwrite it,
    // and will not touch is_private without knowing who owns the row.
  }
  return { ok: true, value: buildExistingPatientValue(prev, row, notes, ownership) };
}

/* -------------------------------------------------------------------------- */
/*  Reorder prefill builders (search ORDERS by name, prefill a fresh draft)    */
/* -------------------------------------------------------------------------- */

const FOOT_UNITS: readonly FootSizeUnit[] = ['EU', 'UK', 'US'];
const INTENDED_USE_VALUES: ReadonlySet<string> = new Set(
  ORTHOTIC_INTENDED_USE_OPTIONS.map((o) => o.value),
);

/** Pull a {value, unit} foot size out of a line.configuration node. */
function footSizeFromConfig(node: unknown): { value: string; unit: FootSizeUnit } {
  if (node && typeof node === 'object') {
    const n = node as { value?: unknown; unit?: unknown };
    const v = typeof n.value === 'number' ? n.value : Number(n.value);
    const unit = FOOT_UNITS.includes(n.unit as FootSizeUnit)
      ? (n.unit as FootSizeUnit)
      : 'EU';
    if (Number.isFinite(v) && v > 0) return { value: String(v), unit };
  }
  return { value: '', unit: 'EU' };
}

const GENDER_VALUES: ReadonlySet<string> = new Set([
  'male',
  'female',
  'other',
  'prefer_not_to_say',
]);

/** Read a numeric age (years) from a config/legacy value, as a form string. */
function ageString(src: unknown): string {
  const n = typeof src === 'number' ? src : Number(src);
  return Number.isFinite(n) && n > 0 && n < 200 ? String(Math.floor(n)) : '';
}

/**
 * Build a fresh PatientForm state from a past order's name + line config.
 * Per-order metrics that live on the line (weight, foot sizes, intended use,
 * shoe brand) carry over, plus any age/gender we can recover - from the
 * structured config (orders created by this reorder flow persist them there)
 * or from the untouched legacy_raw archive (migrated orders). Phone / email
 * stay blank (no patient record to read them from) and observations belong to
 * the prescription. Everything is editable before submit.
 */
export function buildPatientFormFromConfig(
  fullName: string,
  config: Record<string, unknown>,
): PatientFormState {
  const left = footSizeFromConfig(config.left_foot_size);
  const right = footSizeFromConfig(config.right_foot_size);
  const weight =
    typeof config.weight_kg === 'number' && config.weight_kg > 0
      ? String(config.weight_kg)
      : '';
  // A stored valid value still prefills (reuse, reorder, resume). Absent or
  // unknown falls back to EMPTY, never to a default the podiatrist did not
  // choose: the wizard's patient gate then asks for one.
  const intendedUse: PatientFormState['orthotic_intended_use'] =
    typeof config.orthotic_intended_use === 'string' &&
    INTENDED_USE_VALUES.has(config.orthotic_intended_use)
      ? (config.orthotic_intended_use as OrthoticIntendedUse)
      : '';
  const shoeBrand =
    typeof config.shoe_brand === 'string' ? config.shoe_brand.trim() : '';
  // Recover age/gender: structured config first (new reorder orders), then the
  // verbatim legacy_raw archive (migrated orders store them only there).
  const raw = (config.legacy_raw ?? {}) as Record<string, unknown>;
  const age = ageString(config.age) || ageString(raw.age);
  const genderSrc = config.gender ?? raw.gender;
  const gender =
    typeof genderSrc === 'string' && GENDER_VALUES.has(genderSrc)
      ? (genderSrc as PatientFormState['gender'])
      : '';
  return {
    ...EMPTY_PATIENT_FORM,
    full_name: fullName.trim(),
    age,
    gender,
    weight_kg: weight,
    left_foot_size: left.value,
    left_foot_unit: left.unit,
    right_foot_size: right.value,
    right_foot_unit: right.unit,
    orthotic_intended_use: intendedUse,
    shoe_brand: shoeBrand,
  };
}

/**
 * Build the prescription prefill from a past order's line configuration,
 * keeping ONLY the cards the newly-chosen product family will actually render.
 *
 * The region rule itself lives in ./prescriptionReuse as intersectDeepByTarget
 * and is SHARED with the "Use this prescription" button, which used to hold its
 * own copy of the same idea and had drifted into refusing cross-type reuse
 * outright while this path allowed it silently. Prefill only - the source
 * config is never mutated (the shared rule deep-copies every region it keeps).
 *
 * What stays here is what is genuinely local: this path has no per-row control
 * to disable, so an empty intersection just leaves the prescription at its
 * defaults rather than refusing, and it says nothing about dropped regions
 * because the reorder banner already names the source order and product.
 */
export function buildReorderPrescriptionPatch(
  config: Record<string, unknown>,
  targetSubtype: SolutionSubtype | null | undefined,
): Partial<PrescriptionFormState> {
  const patch: Partial<PrescriptionFormState> = {};

  // Podiatrist observations (prescription-level notes) - always safe to carry.
  if (typeof config.observations === 'string' && config.observations.trim()) {
    patch.observations = config.observations;
  }

  const srcDeep =
    config.deep && typeof config.deep === 'object' && !Array.isArray(config.deep)
      ? (config.deep as Record<string, unknown>)
      : {};
  const { deep } = intersectDeepByTarget(srcDeep, targetSubtype);
  if (Object.keys(deep).length > 0) {
    patch.deep = deep as PrescriptionDeepState;
  }
  return patch;
}

/** Build the full reorder payload (patient form + prescription patch) for a
 *  picked past order, intersected against the wizard's chosen subtype. */
export function buildReorderPrefill(
  match: OrderPatientMatch,
  targetSubtype: SolutionSubtype | null | undefined,
): ReorderPrefill {
  const config = match.line.configuration ?? {};
  const patient: PatientPickerValue = {
    mode: 'existing',
    patientId: undefined,
    display_name: match.patient_name,
    hasEdits: false,
    formState: buildPatientFormFromConfig(match.patient_name, config),
    reorderFromOrder: {
      orderId: match.order_id,
      orderNumber: match.order_number,
      createdAt: match.created_at,
      productLabel: formatProductLabel(match.line),
    },
  };
  return {
    patient,
    prescriptionPatch: buildReorderPrescriptionPatch(config, targetSubtype),
  };
}

/** Short, locale-friendly date (e.g. "10 Jun 2026") for the reorder rows. */
function formatReorderDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  try {
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

/**
 * Patient picker - R32: rendered inline as the first section of the
 * prescription form via PatientPickerBody. (The old Card-wrapped
 * standalone `PatientPicker` step component was removed when patient was
 * folded into the prescription step.)
 *
 * The ModeSelector + (New | Existing) panel pair, with its own
 * new/existing mode switch.
 */
export function PatientPickerBody({
  value,
  onChange,
  showErrors,
  clinicId: clinicIdOverride,
  orderClinicId,
  subtype,
  prescriptionHasContent,
  onReorderPrefill,
  isSandal = false,
}: {
  value: PatientPickerValue;
  onChange: (next: PatientPickerValue) => void;
  showErrors?: boolean | undefined;
  /**
   * R35 — when the caller (wizard) lets the user pick a clinic at the
   * action, pass it through so the duplicate-detection + existing-patient
   * search scope to that clinic rather than the user's default.
   */
  clinicId?: string | null;
  /**
   * The clinic the ORDER belongs to. Kept separate from `clinicId` because that
   * one is deliberately widened to null for admins so the SEARCH spans clinics;
   * adopting a record must still be confined to the order's own clinic.
   */
  orderClinicId?: string | null;
  /** The product family chosen in the Subtype step. Drives which prescription
   *  cards a reorder prefill copies over (only the shared ones). */
  subtype?: SolutionSubtype | null | undefined;
  /** True when the prescription form already holds operator-entered content, so
   *  the prior-order modal's reset must confirm before discarding it. */
  prescriptionHasContent?: boolean | undefined;
  /** Emitted when the operator picks a past order to reorder from — carries
   *  both the patient form and the compatible prescription cards so the
   *  wizard can apply them atomically. When absent, the reorder flow falls
   *  back to prefilling the patient form only. */
  onReorderPrefill?: ((next: ReorderPrefill) => void) | undefined;
  /** The ONE sandal flag PrescriptionForm computes (subtype OR line_type
   *  spelling), not re-derived here: the patient form hides the shoe-related
   *  fields by the same rule the payload builders and the wizard gate use. */
  isSandal?: boolean | undefined;
}) {
  const { clinicId: authClinicId, isAdmin } = useAuth();
  // Override wins when provided; otherwise fall back to the auth-derived
  // default. Admins still pass `null` so they see patients across clinics.
  const effectiveClinicId =
    clinicIdOverride !== undefined
      ? clinicIdOverride
      : isAdmin
        ? null
        : authClinicId ?? null;

  const setMode = (mode: PatientMode) => {
    if (mode === value.mode) return;
    const blank: PatientPickerValue = { mode, formState: EMPTY_PATIENT_FORM };
    // If we're leaving an active reorder prefill, clear the prescription it
    // seeded too (via onReorderPrefill with an empty patch) so the other tab
    // starts from a clean slate. A hand-typed prescription is left untouched.
    //
    // Guard on the two things that actually SEED a prescription, never on
    // patientId. Widening this to `|| value.patientId` would route an
    // untouched, hand-typed prescription through the DEFAULT_PRESCRIPTION_STATE
    // rebuild and silently blank it; guarding on reorderFromOrder alone leaves
    // a prescription copied via the prior-order modal (which sets
    // prescriptionSeededFrom, not reorderFromOrder) attached to the NEXT
    // patient. Both seeding paths, and only those, reset here.
    if (shouldResetPrescriptionOnModeSwitch(value) && onReorderPrefill) {
      onReorderPrefill({ patient: blank, prescriptionPatch: {} });
    } else {
      onChange(blank);
    }
  };

  // Task D: prior-order picker modal. We open it whenever the operator
  // selects an existing patient that has at least one prior order on file.
  // Both the existing-patient search panel and the new-patient duplicate
  // banner funnel through this single handler so the modal pops up
  // consistently from either entry point.
  const [priorOrdersFor, setPriorOrdersFor] = React.useState<
    { patientId: string; patientName: string | undefined } | null
  >(null);

  const onExistingPatientPicked = React.useCallback((row: PatientSummaryRow) => {
    if (row.total_orders > 0) {
      setPriorOrdersFor({ patientId: row.id, patientName: row.full_name });
    }
  }, []);

  return (
    <div className="space-y-4">
      {/* Existing-patient = "reorder by name". We search past ORDERS by the
          patient name stored on the line (the patients table is empty for
          migrated history), and picking one prefills this draft. */}
      <p className="text-xs text-text-muted">
        Search by name to find an existing patient record, or a past order for
        someone without one. Picking a record links this order to that patient;
        picking a past order prefills from it.
      </p>
      <ModeSelector mode={value.mode} onChange={setMode} />
      {value.mode === 'new' ? (
        <NewPatientPanel
          value={value}
          onChange={onChange}
          showErrors={showErrors}
          clinicId={effectiveClinicId}
          orderClinicId={orderClinicId ?? null}
          isSandal={isSandal}
          onExistingPatientPicked={onExistingPatientPicked}
        />
      ) : (
        <ExistingPatientPanel
          value={value}
          onChange={onChange}
          showErrors={showErrors}
          isAdmin={isAdmin}
          clinicId={effectiveClinicId}
          orderClinicId={orderClinicId ?? null}
          subtype={subtype ?? null}
          isSandal={isSandal}
          onReorderPrefill={onReorderPrefill}
          onExistingPatientPicked={onExistingPatientPicked}
        />
      )}
      {priorOrdersFor ? (
        <PriorOrderPickerModal
          open={true}
          onOpenChange={(next) => {
            if (!next) setPriorOrdersFor(null);
          }}
          patientId={priorOrdersFor.patientId}
          patientName={priorOrdersFor.patientName}
          targetSubtype={subtype ?? null}
          prescriptionHasContent={prescriptionHasContent ?? false}
          {...(onReorderPrefill
            ? {
                onUsePrescription: (
                  deep: PrescriptionDeepState | undefined,
                  observations?: string | undefined,
                  fromOrderNumber?: string | undefined,
                ) => {
                  // Keep the patient selection exactly as chosen (record-backed,
                  // patientId set) and swap ONLY the prescription. `undefined`
                  // means "start blank", which the wizard-level handler applies
                  // as a deterministic reset from defaults.
                  onReorderPrefill({
                    patient: {
                      ...value,
                      // Mark that THIS panel seeded the prescription, so a later
                      // mode switch resets it instead of carrying one patient's
                      // prescription onto the next. Cleared when nothing was
                      // applied (the "start fresh" reset passes no order).
                      ...(fromOrderNumber
                        ? { prescriptionSeededFrom: fromOrderNumber }
                        : { prescriptionSeededFrom: undefined }),
                    },
                    prescriptionPatch: {
                      ...(deep ? { deep } : {}),
                      ...(observations ? { observations } : {}),
                    },
                    ...(fromOrderNumber ? { appliedFromOrderNumber: fromOrderNumber } : {}),
                  });
                },
              }
            : {})}
        />
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function ModeSelector({
  mode,
  onChange,
}: {
  mode: PatientMode;
  onChange: (m: PatientMode) => void;
}) {
  const { t } = useTranslation('orders');
  const cards: Array<{
    id: PatientMode;
    titleKey: string;
    descKey: string;
    Icon: typeof User;
  }> = [
    { id: 'new', titleKey: 'patient_mode_new_title', descKey: 'patient_mode_new_desc', Icon: UserPlus },
    {
      id: 'existing',
      titleKey: 'patient_mode_existing_title',
      descKey: 'patient_mode_existing_desc',
      Icon: User,
    },
  ];

  return (
    <div role="radiogroup" aria-label="Patient mode" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {cards.map((c) => {
        const active = mode === c.id;
        const Icon = c.Icon;
        return (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`patient-mode-${c.id}`}
            onClick={() => onChange(c.id)}
            className={`text-start p-3 rounded-xl border-2 transition-colors ${
              active
                ? 'border-brand-500 bg-brand-50'
                : 'border-border-subtle hover:border-brand-300 bg-surface-elevated'
            }`}
          >
            <div className="flex items-center gap-3">
              <span
                className={`h-9 w-9 rounded-lg flex items-center justify-center ${
                  active ? 'bg-brand-500 text-white' : 'bg-sand-100 text-text-secondary'
                }`}
              >
                <Icon className="h-4 w-4" />
              </span>
              <div>
                <div className="text-sm font-semibold text-text-primary">{t(c.titleKey)}</div>
                <p className="mt-0.5 text-xs text-text-secondary leading-snug">{t(c.descKey)}</p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function NewPatientPanel({
  value,
  onChange,
  showErrors,
  clinicId,
  orderClinicId,
  isSandal,
  onExistingPatientPicked,
}: {
  value: PatientPickerValue;
  onChange: (next: PatientPickerValue) => void;
  showErrors?: boolean | undefined;
  clinicId: string | null;
  /** The clinic this ORDER is for; see ExistingPatientPanel's copy of this. */
  orderClinicId: string | null;
  /** Forwarded to PatientForm: a sandal hides the intended-use + shoe-brand
   *  fields. */
  isSandal: boolean;
  onExistingPatientPicked?: (row: PatientSummaryRow) => void;
}) {
  const { t } = useTranslation('patient');

  const fullName = (value.formState.full_name ?? '').trim();
  // Only look for duplicates once a plausible full name (first + last) is typed.
  const looksComplete = fullName.split(/\s+/).filter(Boolean).length >= 2;
  const [debounced, setDebounced] = React.useState('');
  const [dismissedFor, setDismissedFor] = React.useState<string | null>(null);
  const [adoptError, setAdoptError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(fullName), 400);
    return () => clearTimeout(id);
  }, [fullName]);

  const dupeQuery = useQuery({
    queryKey: ['wizard', 'patient-dupe', clinicId, debounced],
    queryFn: () =>
      patientQueries.searchPatients(supabase, {
        clinicId,
        search: debounced,
        limit: 5,
      }),
    enabled: !!clinicId && looksComplete && debounced.trim().length >= 3,
  });

  const exactMatches = (dupeQuery.data ?? []).filter(
    (p) => p.full_name.trim().toLowerCase() === fullName.toLowerCase(),
  );
  const showDupe =
    exactMatches.length > 0 && dismissedFor !== fullName.toLowerCase();
  const match = exactMatches[0];

  return (
    <div className="space-y-3">
      <Eyebrow>{t('list_create_button')}</Eyebrow>

      {showDupe && match ? (
        <div className="rounded-lg border border-warning-300 bg-warning-50 p-3 space-y-2">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-warning-700 shrink-0 mt-0.5" aria-hidden="true" />
            <div className="min-w-0 text-sm">
              <p className="font-semibold text-warning-800">
                A patient named “{match.full_name}” already exists
              </p>
              <p className="text-warning-700 text-xs mt-0.5">
                {[
                  match.patient_code,
                  match.phone || null,
                  match.total_orders
                    ? `${match.total_orders} order${match.total_orders === 1 ? '' : 's'}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <p className="text-warning-700 text-xs mt-1">
                Is this the same person? Use their record to add this order to
                their file, or keep creating a new patient.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={() => {
                void (async () => {
                  const adopted = await adoptPatientRecord(value, match, orderClinicId);
                  if (!adopted.ok) {
                    setAdoptError(adopted.reason);
                    return;
                  }
                  setAdoptError(null);
                  onChange(adopted.value);
                  onExistingPatientPicked?.(match);
                })();
              }}
            >
              Use existing record
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setDismissedFor(fullName.toLowerCase())}
            >
              Keep as new
            </Button>
          </div>
        </div>
      ) : null}

      {adoptError ? (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-md border border-warning-300 bg-warning-50 p-2 text-xs text-warning-800"
        >
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
          {adoptError}
        </p>
      ) : null}

      <PatientForm
        value={value.formState}
        onChange={(fs) => {
          const display = (fs.full_name ?? '').trim();
          onChange({
            ...value,
            formState: fs,
            display_name: display || undefined,
          });
        }}
        showErrors={showErrors}
        isSandal={isSandal}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function ExistingPatientPanel({
  value,
  onChange,
  showErrors,
  isAdmin,
  clinicId,
  orderClinicId,
  subtype,
  isSandal,
  onReorderPrefill,
  onExistingPatientPicked,
}: {
  value: PatientPickerValue;
  onChange: (next: PatientPickerValue) => void;
  showErrors?: boolean | undefined;
  /** Admin sees orders across every clinic, so we surface the clinic column. */
  isAdmin: boolean;
  /** Null for admins, which the patient_summary_list RPC reads as "every
   *  clinic I may see" (is_admin() OR current_clinic_ids()). */
  clinicId: string | null;
  /** The clinic this ORDER is for. Unlike `clinicId` this is never widened to
   *  null for admins, because a record from another clinic must not be adopted
   *  onto this order however wide the search was. */
  orderClinicId: string | null;
  subtype: SolutionSubtype | null;
  /** Forwarded to PatientForm: a sandal hides the intended-use + shoe-brand
   *  fields. */
  isSandal: boolean;
  onReorderPrefill?: ((next: ReorderPrefill) => void) | undefined;
  onExistingPatientPicked?: ((row: PatientSummaryRow) => void) | undefined;
}) {
  const { t } = useTranslation(['orders', 'patient']);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');

  React.useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Search past ORDERS by the patient name on the line. RLS scopes the result
  // to the caller's clinic(s) (admin sees all), mirroring the order tracker.
  const searchQuery = useQuery({
    ...orderQueries.searchOrdersByPatientName(supabase, {
      search: debouncedSearch,
      limit: 20,
    }),
    enabled: !value.reorderFromOrder && debouncedSearch.length >= 2,
  });

  // ...AND search the patients TABLE. The order search above only matches
  // `order_lines.patient_name`, which the wizard deliberately leaves NULL for
  // record-backed orders (the name lives on the patients row instead, see
  // buildLinesPayload's `recordlessName`). That made every patient with a real
  // record invisible here - a podiatrist who had just created an order for a
  // patient could not find them, and re-created them instead. Both sources are
  // needed: the patients table for real records, the order history for the
  // migrated name-only past.
  const patientQuery = useQuery({
    queryKey: ['wizard', 'patient-search', clinicId, debouncedSearch],
    queryFn: () =>
      patientQueries.searchPatients(supabase, {
        clinicId,
        search: debouncedSearch,
        limit: 10,
      }),
    // NOTE: deliberately NOT gated on `clinicId` being non-null. Admins have
    // no clinic, and gating on it is exactly what disabled duplicate detection
    // for them; the RPC already resolves a null clinic to the caller's
    // permitted scope.
    enabled: !value.reorderFromOrder && debouncedSearch.length >= 2,
  });

  const [pickError, setPickError] = React.useState<string | null>(null);
  const queryClient = useQueryClient();
  // Where the hydrated weight / foot sizes came from, so the form can say so.
  // A measurement carried forward from an order six months ago is useful, but
  // it must not read as freshly taken.
  const [metricSource, setMetricSource] = React.useState<MetricSource | null>(null);

  const onPickPatientRecord = async (row: PatientSummaryRow) => {
    // Real patients row -> set patientId so the order links to it (no
    // duplicate minted). Prescription state is untouched here; picking a past
    // LINE to prefill from is the prior-order picker's job.
    const adopted = await adoptPatientRecord(value, row, orderClinicId);
    if (!adopted.ok) {
      setPickError(adopted.reason);
      return;
    }
    setPickError(null);

    // Recover the patient's MEASUREMENTS from their order history.
    //
    // buildExistingPatientValue can only fill what the patients table holds,
    // which is name, age and sex. Weight and foot sizes live in
    // order_lines.configuration, so the record pick used to leave them blank
    // and the prior-order modal did not fill them either: its callback passes
    // the patient object through untouched. There was no route to them at all.
    //
    // Only physical facts hydrate. Intended use, shoe brand and units are
    // decisions about THIS order and still default fresh. Failure here is
    // non-fatal: a blank weight is the old behaviour, not a broken pick.
    let next = adopted.value;
    try {
      const lines = await queryClient.fetchQuery(
        patientQueries.patientLineHistoryQuery(supabase, row.id),
      );
      const { metrics, source } = hydrateMetricsFromLines(lines);
      if (Object.keys(metrics).length > 0) {
        next = { ...next, formState: { ...next.formState, ...metrics } };
        setMetricSource(source);
      } else {
        setMetricSource(null);
      }
    } catch {
      setMetricSource(null);
    }

    onChange(next);
    onExistingPatientPicked?.(row);
  };

  const onPick = (match: OrderPatientMatch) => {
    if (onReorderPrefill) {
      onReorderPrefill(buildReorderPrefill(match, subtype));
    } else {
      // Defensive fallback: prefill the patient form only.
      onChange(buildReorderPrefill(match, subtype).patient);
    }
  };

  const onClearPick = () => {
    const blank: PatientPickerValue = { mode: 'existing', formState: EMPTY_PATIENT_FORM };
    if (onReorderPrefill) {
      onReorderPrefill({ patient: blank, prescriptionPatch: {} });
    } else {
      onChange(blank);
    }
  };

  // Hooks must run on EVERY render, so these live above the early return below
  // (the panel returns a different tree once a patient is locked in).
  // Patient records rank above order history: a real record is the canonical
  // identity, and picking it links the new order to that patient instead of
  // minting a duplicate.
  const patientRows = React.useMemo(() => patientQuery.data ?? [], [patientQuery.data]);
  const historyMatches = React.useMemo(() => searchQuery.data ?? [], [searchQuery.data]);

  // ONE ROW PER PERSON.
  //
  // These two lists used to render side by side with nothing comparing them,
  // so the same human could appear twice: once as a record that filled only
  // name/age/sex, and once as an order row that copied everything. Same
  // person, two rows, different behaviour.
  //
  // De-duplication is by FOREIGN KEY, never by name. A name is not an identity
  // (10 same-name groups exist inside a single clinic today), so an order is
  // folded into a record only when orders.patient_id or order_lines.patient_id
  // says so. The line key is the one both RPCs used to ignore, and it is what
  // dissolves the 15 QR-minted duplicates. Orders matching neither key stay
  // visible, grouped per person and labelled as having no record.
  const personRows = React.useMemo(
    () => mergePatientSearchResults(patientRows, historyMatches),
    [patientRows, historyMatches],
  );
  const recordlessClusters = React.useMemo(
    () => personRows.filter((r): r is Extract<typeof r, { kind: 'name_only' }> =>
      r.kind === 'name_only',
    ),
    [personRows],
  );
  // Clinic names for the admin label, harvested from the order-history rows we
  // already fetched - no extra query. A patient with no past order in the
  // result set simply shows no label rather than a wrong one.
  const clinicNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const h of historyMatches) {
      if (h.clinic?.id && h.clinic.name) m.set(h.clinic.id, h.clinic.name);
    }
    return m;
  }, [historyMatches]);

  // The panel has TWO independent sources (patient records + order history).
  // Combining them with `||` made the pair all-or-nothing: either one still
  // loading hid results the other had already returned, and either one failing
  // replaced BOTH lists with an error, so a working source became unusable
  // because of an unrelated failure. Each source now only speaks for itself:
  // we block on both loading, error out only when both failed, and otherwise
  // render whatever did come back with an inline note about the part that did
  // not.
  const bothLoading = searchQuery.isLoading && patientQuery.isLoading;
  const bothFailed = searchQuery.isError && patientQuery.isError;
  const bothSettled = !searchQuery.isLoading && !patientQuery.isLoading;
  const partialFailure =
    !bothFailed && (searchQuery.isError || patientQuery.isError);
  const partialFailureLabel = searchQuery.isError
    ? t('orders:wizard_patient_search_partial_orders')
    : t('orders:wizard_patient_search_partial_records');

  // Once a patient is locked in, show the prefilled (editable) form. Two ways
  // in: (a) a reorder pick (value.reorderFromOrder) - no patient record, name
  // rides on the order; (b) an existing record selected via the New-patient
  // duplicate banner (value.patientId) - a real patients row. We MUST handle
  // the patientId case here too, otherwise the dupe banner's "Use existing
  // record" sets patientId but the panel falls back to the search view, hiding
  // the form while validation silently blocks Continue (dead end).
  if (value.reorderFromOrder || value.patientId) {
    const src = value.reorderFromOrder;
    return (
      <div className="space-y-4">
        {src ? (
          <div className="rounded-lg border border-brand-200 bg-brand-50 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <History className="h-4 w-4 text-brand-600 shrink-0 mt-0.5" aria-hidden="true" />
                <div className="min-w-0 text-sm">
                  <p className="font-semibold text-text-primary">
                    Reordering for {value.display_name || value.formState.full_name}
                  </p>
                  <p className="text-text-secondary text-xs mt-0.5">
                    Prefilled from order {src.orderNumber} ({formatReorderDate(src.createdAt)}
                    {src.productLabel ? ` · ${src.productLabel}` : ''}). Review and adjust
                    anything below before you submit. A new order is created; the past one is
                    not changed.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClearPick}
                startIcon={<Search className="h-4 w-4" />}
              >
                Pick another
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-end justify-between gap-3">
            <div>
              <Eyebrow>Existing patient</Eyebrow>
              <p className="text-sm text-text-secondary mt-1">
                Review the details below. Any changes are saved to the patient
                record when you submit the order.
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClearPick}
              startIcon={<Search className="h-4 w-4" />}
            >
              Pick another
            </Button>
          </div>
        )}
        {metricSource ? (
          // Says where the numbers came from. A weight carried forward from an
          // order months ago is genuinely useful, but it must not read as if it
          // were measured today: the podiatrist decides whether to re-measure.
          <p className="text-xs text-text-muted mb-3">
            Weight and foot sizes carried from order{' '}
            <span className="font-mono">{metricSource.orderNumber}</span>
            {' ('}
            {formatReorderDate(metricSource.createdAt)}
            {'). Check they are still current.'}
          </p>
        ) : null}
        <PatientForm
          value={value.formState}
          onChange={(fs) => {
            const display = (fs.full_name ?? '').trim();
            onChange({
              ...value,
              formState: fs,
              display_name: display || undefined,
              hasEdits: true,
            });
          }}
          showErrors={showErrors}
          isSandal={isSandal}
        />
        {!src && value.hasEdits ? (
          <p className="text-xs text-text-muted">
            Edits will be saved to the patient record on order submission.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search
          aria-hidden="true"
          className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted"
        />
        <Input
          type="search"
          placeholder={t('orders:wizard_reorder_search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ps-9"
          autoFocus
        />
      </div>

      {pickError ? (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-md border border-warning-300 bg-warning-50 p-2 text-xs text-warning-800"
        >
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
          {pickError}
        </p>
      ) : null}

      <div className="space-y-2">
        {debouncedSearch.length < 2 ? (
          <EmptyState
            icon={<History className="h-6 w-6" />}
            title={t('orders:wizard_reorder_prompt_title')}
            description={t('orders:wizard_reorder_prompt_body')}
          />
        ) : bothLoading ? (
          <>
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </>
        ) : bothFailed ? (
          <EmptyState
            icon={<AlertCircle className="h-6 w-6" />}
            title={t('orders:wizard_patient_search_error_title')}
            description={String(
              (searchQuery.error as { message?: string } | undefined)?.message ??
                (patientQuery.error as { message?: string } | undefined)?.message ??
                'Please try again.',
            )}
          />
        ) : bothSettled &&
          !partialFailure &&
          historyMatches.length === 0 &&
          patientRows.length === 0 ? (
          // `!partialFailure` matters: one source erroring while the other
          // legitimately returns zero rows must NOT read as "no such patient",
          // or the operator creates a duplicate - the very bug this panel
          // exists to fix. That case falls through to the notice below.
          <EmptyState
            icon={<User className="h-6 w-6" />}
            title={t('orders:wizard_reorder_no_results')}
            description={t('orders:wizard_reorder_no_results_body')}
          />
        ) : (
          <>
            {partialFailure ? (
              <p
                role="status"
                className="flex items-start gap-2 rounded-md border border-warning-300 bg-warning-50 p-2 text-xs text-warning-800"
              >
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                {partialFailureLabel}
              </p>
            ) : null}
            <div className="max-h-[340px] overflow-y-auto rounded-md border border-border-subtle shadow-inner">
            {patientRows.length > 0 ? (
              <ul className="divide-y divide-border-subtle">
                {patientRows.map((p) => (
                  <li key={`patient-${p.id}`}>
                    <button
                      type="button"
                      onClick={() => onPickPatientRecord(p)}
                      data-testid={`patient-row-${p.id}`}
                      className="w-full text-start p-3 hover:bg-sand-100 transition-colors"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium text-text-primary truncate">
                          {p.full_name}
                        </span>
                        <span className="shrink-0 rounded-full bg-brand-100 text-brand-700 px-1.5 py-0.5 text-[10px] font-medium">
                          Patient record
                        </span>
                      </div>
                      <div className="text-xs text-text-muted mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                        {p.patient_code ? (
                          <span className="font-mono">{p.patient_code}</span>
                        ) : null}
                        <span className="inline-flex items-center gap-1">
                          <History className="h-3.5 w-3.5" aria-hidden="true" />
                          {p.total_orders === 1 ? '1 past order' : `${p.total_orders} past orders`}
                        </span>
                        {p.last_order_date ? (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                            {formatReorderDate(p.last_order_date)}
                          </span>
                        ) : null}
                        {/* Admins search across EVERY clinic (clinicId null),
                            so without this two same-named patients from
                            different clinics are indistinguishable. Mirrors
                            the order-history rows below. */}
                        {isAdmin && clinicNameById.get(p.clinic_id) ? (
                          <span className="inline-flex items-center gap-1">
                            <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                            {clinicNameById.get(p.clinic_id)}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {/* Record-less orders, grouped so one person is one group. Every
                order stays individually pickable: collapsing them to a single
                row would remove the ability to reuse a SPECIFIC older order,
                which is a capability in use today. */}
            <ul className="divide-y divide-border-subtle">
              {recordlessClusters.map((cluster) => (
                <li key={cluster.id}>
                  <div className="px-3 pt-3 pb-1 text-xs text-text-muted">
                    <span className="font-medium text-text-secondary">
                      {cluster.displayName}
                    </span>
                    {' · '}
                    {cluster.orderCount === 1 ? '1 past order' : `${cluster.orderCount} past orders`}
                    {' · no record on file'}
                  </div>
                  <ul>
              {cluster.orderIndexes.map((idx) => historyMatches[idx]).filter((m): m is OrderPatientMatch => !!m).map((m) => (
                <li key={m.line.id}>
                  <button
                    type="button"
                    onClick={() => onPick(m)}
                    data-testid={`reorder-row-${m.line.id}`}
                    className="w-full text-start p-3 hover:bg-sand-100 transition-colors"
                  >
                    <div className="font-medium text-text-primary truncate">
                      {m.patient_name}
                    </div>
                    <div className="text-xs text-text-muted mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                        {formatReorderDate(m.created_at)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-text-secondary">
                        <History className="h-3.5 w-3.5" aria-hidden="true" />
                        {formatProductLabel(m.line)}
                      </span>
                      {isAdmin && m.clinic?.name ? (
                        <span className="inline-flex items-center gap-1">
                          <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                          {m.clinic.name}
                        </span>
                      ) : null}
                      <span className="font-mono">{m.order_number}</span>
                    </div>
                  </button>
                </li>
              ))}
                  </ul>
                </li>
              ))}
            </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
